import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { LuLightbulb, LuCheckCircle, LuInfo, LuDog, LuCat, LuBird, LuBug } from 'react-icons/lu';
import { planosAPI, formatters } from '../services/api';
import { toast } from 'react-toastify';
import { PET_PLAN_FALLBACK_PRICES } from '../utils/pricing';

const iconStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' };
const LightbulbIcon = () => <LuLightbulb size={14} style={iconStyle} />;
const CheckCircleIcon = () => <LuCheckCircle size={14} style={iconStyle} />;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.grayMedium};
  }

  &:disabled {
    background-color: #f8f9fa;
    color: ${props => props.theme.colors.grayMedium};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }

  &:disabled {
    background-color: #f8f9fa;
    color: ${props => props.theme.colors.grayMedium};
    cursor: not-allowed;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:hover {
    border-color: ${props => props.theme.colors.secondary};
  }
  
  input[type="radio"] {
    margin: 0;
    accent-color: ${props => props.theme.colors.secondary};
  }
  
  ${props => props.checked && `
    border-color: ${props.theme.colors.secondary};
    background-color: rgba(59, 130, 246, 0.05);
  `}
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  margin-top: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const InfoNote = styled.small`
  color: ${props => props.theme.colors.grayMedium};
  display: block;
  margin-top: 5px;
  font-size: 12px;
  font-style: italic;
`;

const WeightEstimateNote = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  padding: 10px;
  margin-top: 8px;
  font-size: 13px;
  color: ${props => props.theme.colors.grayDark};
`;

// Componentes para melhor UX
const PetTypeSection = styled.div`
  margin-bottom: 25px;
  padding: 20px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 12px;
  border: 2px solid ${props => props.theme.colors.borderLight};
`;

const PetTypeTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PetTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PetTypeOption = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border: 2px solid ${props => props.selected ? props.theme.colors.secondary : props.theme.colors.border};
  border-radius: 10px;
  background: ${props => props.selected ? props.theme.colors.lightBlue : props.theme.colors.white};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.secondary};
  }

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const PetIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.selected ? props.theme.colors.secondary : props.theme.colors.grayMedium};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: white;
  transition: background-color 0.2s ease;
`;

const PetTypeLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.selected ? props.theme.colors.secondary : props.theme.colors.grayDark};
  text-align: center;
  transition: all 0.3s ease;
`;

const PetTypeDescription = styled.small`
  font-size: 11px;
  color: ${props => props.theme.colors.grayMedium};
  text-align: center;
  margin-top: 4px;
  display: block;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border};
  margin: 30px 0;
`;

const InfoTooltip = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 8px;
  cursor: help;
  
  &:hover::after {
    content: "${props => props.tooltip}";
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background: ${props => props.theme.colors.grayDark};
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    box-shadow: ${props => props.theme.shadows.lg};
    
    &::before {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: ${props => props.theme.colors.grayDark};
    }
  }
`;

// Dados das raças por grupo (mantido do original)
const PET_BREEDS = {
  cao: [
    // Pequeno porte
    'Yorkshire Terrier', 'Chihuahua', 'Pinscher', 'Maltês', 'Shih Tzu', 'Lhasa Apso', 
    'Pug', 'Jack Russell Terrier', 'Cavalier King Charles Spaniel', 'Papillon',
    'Pomerânia', 'West Highland White Terrier', 'Cairn Terrier', 'Dachshund (Salsicha)',
    
    // Médio porte
    'Cocker Spaniel', 'Border Collie', 'Beagle', 'Boxer', 'Bulldog Inglês', 
    'Bull Terrier', 'Shar Pei', 'Chow Chow', 'Schnauzer', 'Basset Hound',
    'Australian Shepherd', 'Brittany', 'Pointer', 'Setter Inglês', 'Siberian Husky',
    
    // Grande porte
    'Labrador', 'Golden Retriever', 'Pastor Alemão', 'Rottweiler', 'Doberman',
    'Fila Brasileiro', 'São Bernardo', 'Dogue Alemão', 'Mastiff', 'Newfoundland',
    'Bernese Mountain Dog', 'Akbash', 'Bloodhound', 'Great Dane', 'Irish Wolfhound',
    
    // SRD
    'SRD (Sem Raça Definida)'
  ],
  gato: [
    'Persa', 'Siamês', 'Maine Coon', 'Ragdoll', 'British Shorthair', 'Scottish Fold',
    'Sphynx', 'Bengal', 'Abissínio', 'Russian Blue', 'Birmanês', 'Angora Turco',
    'Oriental', 'Cornish Rex', 'Devon Rex', 'Norwegian Forest', 'Somali', 'Chartreux',
    'Burmês', 'Tonquinês', 'Manx', 'Curl Americano', 'Bobtail Japonês', 'Korat',
    'SRD (Sem Raça Definida)'
  ],
  ave: [
    'Canário', 'Periquito', 'Papagaio', 'Calopsita', 'Bem-te-vi', 'Sabiá',
    'Curió', 'Bicudo', 'Azulão', 'Trinca-ferro', 'Uirapuru', 'Pintassilgo',
    'Cardeal', 'Tico-tico', 'João-de-barro', 'Sanhaço', 'Corruíra', 'Coleiro',
    'Agapornis', 'Cacatua', 'Arara', 'Periquito-australiano', 'Canário-belga',
    'Mandarim', 'Outras'
  ],
  exotico: [
    'Iguana', 'Tartaruga', 'Cobra', 'Hamster', 'Coelho', 'Porquinho da Índia',
    'Furão', 'Chinchila', 'Lagarto', 'Camaleão', 'Gecko', 'Outros'
  ]
};

// Estimativas de peso por porte (expandido)
const WEIGHT_ESTIMATES = {
  cao: {
    pequeno: '1-10 kg (Yorkshire, Chihuahua, Pug...)',
    medio: '10-25 kg (Cocker, Beagle, Border Collie...)',
    grande: '25+ kg (Labrador, Pastor Alemão, Rottweiler...)'
  },
  gato: {
    pequeno: '2-4 kg (maioria dos gatos domésticos)',
    medio: '4-7 kg (Maine Coon, Ragdoll...)',
    grande: '7+ kg (raças grandes ou com sobrepeso)'
  },
  ave: {
    pequeno: '0.01-0.1 kg (Canário, Pintassilgo...)',
    medio: '0.1-0.5 kg (Calopsita, Periquito...)',
    grande: '0.5+ kg (Papagaio, Cacatua, Arara...)'
  },
  exotico: {
    pequeno: '0.1-1 kg (Hamster, Pequenos répteis...)',
    medio: '1-5 kg (Coelho, Porquinho da Índia...)',
    grande: '5+ kg (Iguana, Tartaruga grande...)'
  }
};

// Pesos médios para preenchimento automático
const AVERAGE_WEIGHTS = {
  cao: {
    pequeno: 5.5,
    medio: 17.5,
    grande: 35
  },
  gato: {
    pequeno: 3,
    medio: 5.5,
    grande: 8
  },
  ave: {
    pequeno: 0.05,
    medio: 0.3,
    grande: 1
  },
  exotico: {
    pequeno: 0.5,
    medio: 3,
    grande: 7
  }
};

const PetForm = ({ onDataChange, data = {}, error, selectedPlan, petId }) => {
  // Garantir que temos um ID único para este formulário
  const formId = petId || data.id || `temp-${Date.now()}`;
  
  const [petData, setPetData] = useState({
    name: '',
    group: '', // cao, gato, ave, exotico
    size: '', // pequeno, medio, grande
    age: '',
    weight: '',
    breed: '',
    sex: '', // macho, femea
    service: '',
    castrated: false,
    microchip: false,
    specialNeeds: '',
    ...data
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    let mounted = true;
    planosAPI
      .getValores()
      .then((res) => {
        const data = res.data?.data?.pricing || null;
        if (mounted) setPricing(data);
      })
      .catch((err) => {
        console.error('Erro ao carregar valores de planos:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Função para obter peso médio
  const getAverageWeight = useCallback(() => {
    if (!petData.group || !petData.size) return null;
    return AVERAGE_WEIGHTS[petData.group]?.[petData.size] || null;
  }, [petData.group, petData.size]);

  // Gerar opções de serviço para pets (apenas quando o plano NÃO é exclusivo para pets)
  const getPetServices = () => {
    const baseServices = [
      { value: '', label: 'Selecione o serviço...' }
    ];

    const services = [...baseServices];
    
    // Buscar valores de pets do pricing.dependentes[idPlano].pets
    if (pricing && pricing.dependentes && selectedPlan && selectedPlan.idPlano) {
      const idPlano = String(selectedPlan.idPlano);
      const dependentesPlano = pricing.dependentes[idPlano];
      
      if (dependentesPlano && dependentesPlano.pets) {
        const petsPricing = dependentesPlano.pets;
        
        // Iterar sobre os planos PET disponíveis (idPlano como chave)
        Object.keys(petsPricing).forEach(petPlanId => {
          const petPlan = petsPricing[petPlanId];
          
          // Nova estrutura: { desc: "NOME DO PLANO", valor: 10 }
          if (petPlan && petPlan.desc && petPlan.valor !== undefined) {
            const serviceName = petPlan.desc;
            const valor = petPlan.valor;
            const price = formatters.formatCurrency(valor);
            
            // Criar slug para o value baseado no idPlano
            const serviceValue = `pet-plan-${petPlanId}`;
            
            services.push({
              value: serviceValue,
              label: `${serviceName} - ${price}/mês`,
              serviceName: serviceName, // Nome completo para referência
              petPlanId: petPlanId, // ID do plano PET
              valor: valor
            });
          }
        });
      }
    }
    
    // Fallback se não tiver no pricing
    if (services.length === 1) {
      services.push(
        ...Object.entries(PET_PLAN_FALLBACK_PRICES).map(([petPlanId, { valor, desc }]) => ({
          value: `pet-plan-${petPlanId}`,
          label: `${desc} - ${formatters.formatCurrency(valor)}/mês`,
          serviceName: desc,
          petPlanId,
          valor
        }))
      );
    }

    return services;
  };

  const petServices = getPetServices();
  
  // Verificar se o campo de serviço deve ser exibido (apenas quando o plano NÃO é exclusivo para pets)
  const shouldShowServiceField = selectedPlan && !selectedPlan.somentePET;

  // Opções de tipos de pets com descrições (do PetForm2.js)
  const petTypes = [
    {
      value: 'cao',
      label: 'Cão',
      icon: LuDog,
      description: 'Cães de todas as raças'
    },
    {
      value: 'gato',
      label: 'Gato',
      icon: LuCat,
      description: 'Gatos domésticos'
    },
    {
      value: 'ave',
      label: 'Ave',
      icon: LuBird,
      description: 'Pássaros e aves'
    },
    {
      value: 'exotico',
      label: 'Exótico',
      icon: LuBug,
      description: 'Répteis e outros'
    }
  ];

  // Usar ref para evitar loop infinito
  const onDataChangeRef = useRef(onDataChange);
  
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    onDataChangeRef.current(petData);
  }, [petData]);

  const handleInputChange = (field, value) => {
    // Validar se está tentando selecionar o porte sem ter selecionado a espécie
    if (field === 'size' && !petData.group) {
      toast.warning('Selecione primeiro a espécie do animal antes de escolher o porte.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setPetData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Limpar campos relacionados quando o grupo mudar
      if (field === 'group' && value !== prev.group) {
        updated.size = '';
        updated.breed = '';
        updated.weight = '';
      }
      
      // Limpar raça e preencher peso médio quando o porte mudar
      if (field === 'size' && value !== prev.size && updated.group) {
        updated.breed = '';
        // Calcular peso médio diretamente usando os valores atualizados
        const avgWeight = AVERAGE_WEIGHTS[updated.group]?.[value];
        updated.weight = avgWeight ? avgWeight.toString() : '';
      }
      
      return updated;
    });
    
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };


  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value.trim() !== '';
      case 'group':
        return value !== '';
      case 'size':
        return value !== '';
      case 'age':
        const age = parseInt(value);
        return age >= 0 && age <= 30;
      case 'weight':
        const weight = parseFloat(value);
        return weight > 0;
      case 'breed':
        return value.trim() !== '';
      case 'sex':
        return value !== '';
      case 'service':
        // Obrigatório apenas quando o plano NÃO é exclusivo para pets
        if (shouldShowServiceField) {
          return value !== '';
        }
        return true; // Não obrigatório para planos exclusivos de pets
      default:
        return true;
    }
  };

  const handleBlur = (field) => {
    const isValid = validateField(field, petData[field]);
    setFieldErrors(prev => ({
      ...prev,
      [field]: !isValid
    }));
  };

  const getAvailableBreeds = useCallback(() => {
    if (!petData.group) return [];
    return PET_BREEDS[petData.group] || [];
  }, [petData.group]);

  const getWeightEstimate = useCallback(() => {
    if (!petData.group || !petData.size) return null;
    return WEIGHT_ESTIMATES[petData.group]?.[petData.size] || null;
  }, [petData.group, petData.size]);

  return (
    <>
      {/* Seção de seleção do tipo de pet (melhorada do PetForm2.js) */}
      <PetTypeSection>
        <PetTypeTitle>
          <LuDog size={18} /> Tipo de Pet <span className="required">*</span>
          <InfoTooltip tooltip="Selecione a categoria do seu pet para melhor atendimento">
            <LuInfo size={14} />
          </InfoTooltip>
        </PetTypeTitle>
        <PetTypeGrid>
          {petTypes.map((type) => (
            <PetTypeOption
              key={type.value}
              selected={petData.group === type.value}
            >
              <input
                type="radio"
                name={`petGroup-${formId}`}
                value={type.value}
                checked={petData.group === type.value}
                onChange={(e) => handleInputChange('group', e.target.value)}
                onBlur={() => handleBlur('group')}
                required
              />
              <PetIcon selected={petData.group === type.value}>
                <type.icon size={20} />
              </PetIcon>
              <PetTypeLabel selected={petData.group === type.value}>
                {type.label}
              </PetTypeLabel>
              <PetTypeDescription>
                {type.description}
              </PetTypeDescription>
            </PetTypeOption>
          ))}
        </PetTypeGrid>
        <ErrorMessage show={fieldErrors.group}>
          Por favor, selecione o tipo de animal
        </ErrorMessage>
      </PetTypeSection>

      <SectionDivider />

      <FormRow>
        <FormGroup>
          <Label htmlFor="petName">
            Nome do Pet <span className="required">*</span>
          </Label>
          <Input
            type="text"
            id="petName"
            value={petData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder="Nome do seu pet"
            $error={fieldErrors.name}
            required
          />
          <ErrorMessage show={fieldErrors.name}>
            Por favor, informe o nome do pet
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="petAge">
            Idade do Pet (anos) <span className="required">*</span>
          </Label>
          <Input
            type="number"
            id="petAge"
            value={petData.age}
            onChange={(e) => handleInputChange('age', e.target.value)}
            onBlur={() => handleBlur('age')}
            min="0"
            max="30"
            placeholder="Idade em anos"
            $error={fieldErrors.age}
            required
          />
          <ErrorMessage show={fieldErrors.age}>
            Por favor, informe uma idade válida (0-30 anos)
          </ErrorMessage>
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <Label>
            Porte do Animal <span className="required">*</span>
          </Label>
          <RadioGroup>
            <RadioOption checked={petData.size === 'pequeno'}>
              <input
                type="radio"
                name={`petSize-${formId}`}
                value="pequeno"
                checked={petData.size === 'pequeno'}
                onChange={(e) => handleInputChange('size', e.target.value)}
              />
              Pequeno
            </RadioOption>
            <RadioOption checked={petData.size === 'medio'}>
              <input
                type="radio"
                name={`petSize-${formId}`}
                value="medio"
                checked={petData.size === 'medio'}
                onChange={(e) => handleInputChange('size', e.target.value)}
              />
              Médio
            </RadioOption>
            <RadioOption checked={petData.size === 'grande'}>
              <input
                type="radio"
                name={`petSize-${formId}`}
                value="grande"
                checked={petData.size === 'grande'}
                onChange={(e) => handleInputChange('size', e.target.value)}
              />
              Grande
            </RadioOption>
          </RadioGroup>
          {!petData.group && (
            <InfoNote>Selecione primeiro o tipo de animal</InfoNote>
          )}
          <ErrorMessage show={fieldErrors.size}>
            Por favor, selecione o porte do animal
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="petWeight">
            Peso (kg) <span className="required">*</span>
          </Label>
          <Input
            type="number"
            id="petWeight"
            value={petData.weight}
            onChange={(e) => handleInputChange('weight', e.target.value)}
            onBlur={() => handleBlur('weight')}
            min="0"
            step="0.1"
            placeholder="Peso em kg"
            $error={fieldErrors.weight}
            required
          />
          {getWeightEstimate() && (
            <WeightEstimateNote>
              <LightbulbIcon /> Estimativa para {petData.size} porte: {getWeightEstimate()}
              {petData.weight && getAverageWeight() && parseFloat(petData.weight) === getAverageWeight() && (
                <span style={{ display: 'block', marginTop: '4px', fontWeight: 'bold', color: '#059669' }}>
                  <CheckCircleIcon /> Peso preenchido automaticamente com a média
                </span>
              )}
            </WeightEstimateNote>
          )}
          <ErrorMessage show={fieldErrors.weight}>
            Por favor, informe o peso do pet
          </ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="petBreed">
            Raça <span className="required">*</span>
          </Label>
          <Select
            id="petBreed"
            value={petData.breed}
            onChange={(e) => handleInputChange('breed', e.target.value)}
            onBlur={() => handleBlur('breed')}
            $error={fieldErrors.breed}
            disabled={!petData.group}
            required
          >
            <option value="">Selecione a raça...</option>
            {getAvailableBreeds().map(breed => (
              <option key={breed} value={breed}>
                {breed}
              </option>
            ))}
          </Select>
          {!petData.group && (
            <InfoNote>Selecione primeiro o tipo de animal</InfoNote>
          )}
          <ErrorMessage show={fieldErrors.breed}>
            Por favor, selecione a raça do pet
          </ErrorMessage>
        </FormGroup>
        
        <FormGroup>
          <Label>
            Sexo do Animal <span className="required">*</span>
          </Label>
          <RadioGroup>
            <RadioOption checked={petData.sex === 'macho'}>
              <input
                type="radio"
                name={`petSex-${formId}`}
                value="macho"
                checked={petData.sex === 'macho'}
                onChange={(e) => handleInputChange('sex', e.target.value)}
                onBlur={() => handleBlur('sex')}
                required
              />
              Macho
            </RadioOption>
            <RadioOption checked={petData.sex === 'femea'}>
              <input
                type="radio"
                name={`petSex-${formId}`}
                value="femea"
                checked={petData.sex === 'femea'}
                onChange={(e) => handleInputChange('sex', e.target.value)}
                onBlur={() => handleBlur('sex')}
                required
              />
              Fêmea
            </RadioOption>
          </RadioGroup>
          <ErrorMessage show={fieldErrors.sex}>
            Por favor, selecione o sexo do animal
          </ErrorMessage>
        </FormGroup>
      </FormRow>
      
      {/* Campo de Tipo de Serviço - Apenas para planos que NÃO são exclusivos para pets */}
      {shouldShowServiceField && (
        <FormRow>
          <FormGroup>
            <Label htmlFor="petService">
              Tipo de Serviço <span className="required">*</span>
            </Label>
            <Select
              id="petService"
              value={petData.service}
              onChange={(e) => handleInputChange('service', e.target.value)}
              onBlur={() => handleBlur('service')}
              $error={fieldErrors.service}
              required
            >
              {petServices.map(service => (
                <option key={service.value} value={service.value}>
                  {service.label}
                </option>
              ))}
            </Select>
            <ErrorMessage show={fieldErrors.service}>
              Por favor, selecione o tipo de serviço
            </ErrorMessage>
            <InfoNote>
              Escolha o tipo de serviço funerário para o seu pet
            </InfoNote>
          </FormGroup>
        </FormRow>
      )}
    </>
  );
};

export default PetForm;